import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";

export default function Cart() {
  const { items, updateItem, removeItem, subtotal, count, loading } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const shipping = subtotal >= 999 ? 0 : subtotal > 0 ? 79 : 0;
  const total = subtotal + shipping;

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="cart-page">
        <ShoppingBag className="h-16 w-16 text-rani mx-auto mb-6" />
        <h1 className="font-display text-4xl text-deepbrown">Your cart is waiting</h1>
        <p className="text-deepbrown/60 mt-2">Sign in to view items in your bag.</p>
        <Link to="/login" className="inline-block mt-6 px-8 py-3 rounded-full bg-rani text-white font-bold" data-testid="cart-login-btn">Sign In</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12" data-testid="cart-page">
      <h1 className="font-display text-4xl sm:text-5xl text-deepbrown font-bold">Your Bag {count > 0 && <span className="text-rani">({count})</span>}</h1>

      {loading ? (
        <div className="mt-10 text-deepbrown/60">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl p-16 mt-10 text-center border border-deepbrown/10">
          <p className="font-display text-2xl text-deepbrown">Your cart is empty</p>
          <p className="text-deepbrown/60 mt-2">Treat yourself to something handmade.</p>
          <Link to="/shop" className="inline-block mt-6 px-7 py-3 rounded-full bg-rani text-white font-bold" data-testid="cart-shop-btn">Shop Now</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-10">
          <div className="lg:col-span-8 space-y-4">
            {items.map((it) => (
              <div key={it.id} className="bg-white rounded-2xl p-4 flex gap-4 items-center border border-deepbrown/10" data-testid={`cart-item-${it.product_id}`}>
                <img src={it.product.images[0]} alt={it.product.name} className="h-24 w-24 rounded-xl object-cover" />
                <div className="flex-1">
                  <Link to={`/product/${it.product.id}`} className="font-display text-lg text-deepbrown hover:text-rani">{it.product.name}</Link>
                  <p className="text-xs text-deepbrown/60 mt-1">
                    {it.color && <>Color: <b>{it.color}</b></>}{it.color && it.size && " · "}{it.size && <>Size: <b>{it.size}</b></>}
                  </p>
                  <p className="font-display text-rani font-bold text-lg mt-1">₹{it.product.price.toLocaleString("en-IN")}</p>
                </div>
                <div className="flex flex-col items-end gap-3">
                  <div className="flex items-center bg-ivory rounded-full border border-deepbrown/20">
                    <button onClick={() => updateItem(it.id, it.quantity - 1)} className="h-9 w-9 grid place-items-center hover:text-rani" data-testid={`cart-decrease-${it.product_id}`}>
                      <Minus className="h-3 w-3" />
                    </button>
                    <span className="w-8 text-center text-sm font-bold">{it.quantity}</span>
                    <button onClick={() => updateItem(it.id, it.quantity + 1)} className="h-9 w-9 grid place-items-center hover:text-rani" data-testid={`cart-increase-${it.product_id}`}>
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>
                  <button onClick={() => removeItem(it.id)} className="text-deepbrown/50 hover:text-rani" data-testid={`cart-remove-${it.product_id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <aside className="lg:col-span-4">
            <div className="bg-white rounded-2xl p-6 border border-deepbrown/10 sticky top-24">
              <h3 className="font-display text-2xl text-deepbrown mb-4">Order Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-deepbrown/70">Subtotal</span><span className="font-bold">₹{subtotal.toLocaleString("en-IN")}</span></div>
                <div className="flex justify-between"><span className="text-deepbrown/70">Shipping</span><span className="font-bold">{shipping === 0 ? <span className="text-emerald">FREE</span> : `₹${shipping}`}</span></div>
                {subtotal < 999 && (
                  <p className="text-xs text-marigold bg-marigold/10 rounded-lg p-2">Add ₹{(999 - subtotal).toLocaleString("en-IN")} more for free shipping ✨</p>
                )}
              </div>
              <div className="border-t border-deepbrown/10 mt-4 pt-4 flex justify-between items-end">
                <span className="font-display text-lg">Total</span>
                <span className="font-display text-3xl text-rani font-bold">₹{total.toLocaleString("en-IN")}</span>
              </div>
              <button
                onClick={() => navigate("/checkout")}
                className="w-full mt-6 inline-flex items-center justify-center gap-2 py-4 rounded-full bg-rani text-white font-bold shadow-lg hover:scale-[1.02] transition-transform"
                data-testid="checkout-btn"
              >
                Checkout <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
